import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthProvider";
import { apiGet } from "../../lib/api-client";
import { useToast } from "../../components/Toast";
import { useCalendarApi } from "./useCalendarApi";
import WeekGrid from "./WeekGrid";
import AppointmentDialog, { PatientOption } from "./AppointmentDialog";
import SettingsDialog from "./SettingsDialog";
import type { Appointment, CreateAppointmentPayload, EditScope, InvoicingSettings, SchedulingSettings, WeekResponse } from "./types";
import { weekDays } from "./time";
import "./Calendar.css";

const EMPTY_WEEK: WeekResponse = { appointments: [], series: {}, warnings: [] };

export default function CalendarPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const api = useCalendarApi();
  const { ensureToken } = useAuth();

  const [anchor, setAnchor] = useState(() => new Date());
  const [settings, setSettings] = useState<SchedulingSettings | null>(null);
  const [invoicing, setInvoicing] = useState<InvoicingSettings | null>(null);
  const [week, setWeek] = useState<WeekResponse>(EMPTY_WEEK);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Appointment | null>(null);
  const [createAt, setCreateAt] = useState<Date | null>(null);
  const [createDur, setCreateDur] = useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [gcal, setGcal] = useState<{ connected: boolean; failed_count: number } | null>(null);

  const days = useMemo(() => weekDays(anchor), [anchor]);
  const fromIso = days[0].toISOString();
  const toIso = useMemo(() => {
    const end = new Date(days[6]);
    end.setDate(end.getDate() + 1);
    return end.toISOString();
  }, [days]);

  const surfaceWarnings = useCallback(
    (warnings: string[] | undefined) => {
      (warnings ?? []).forEach((w) =>
        // Known codes translate; unknown codes surface raw — never dropped.
        toast.warning(t(`calendar.warnings.${w}`, { defaultValue: w })),
      );
    },
    [toast, t],
  );

  const refresh = useCallback(async () => {
    try {
      const data = await api.loadWeek(fromIso, toIso);
      setWeek(data);
      setLoadError(null);
      surfaceWarnings(data.warnings);
    } catch (e: any) {
      setLoadError(e.message);
      toast.error(t("calendar.errors.loadFailed", { msg: e.message }));
    }
  }, [api, fromIso, toIso, surfaceWarnings, toast, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.loadSettings();
        setSettings(s);
      } catch (e: any) {
        toast.error(t("calendar.errors.loadFailed", { msg: e.message }));
      }
      try {
        setInvoicing(await api.loadInvoicing());
      } catch (e: any) {
        // Loud but scoped: invoicing being unreachable (e.g. backend not yet
        // deployed) must not read as "the calendar is broken" — the calendar
        // works fully without invoicing; the dialog just hides the section.
        toast.error(t("calendar.invoicing.failed", { msg: e.message }));
      }
      try {
        const getToken = async () => {
          const tok = await ensureToken();
          if (!tok) throw new Error("no token");
          return tok;
        };
        // GET /patients returns {"items": [...]} (see get_patients.py / ListPatientsResponse)
        const r = await apiGet<{ items: { patient_id: string; name: string }[] }>(
          "/patients", getToken,
        );
        setPatients((r.items || []).map((p) => ({ patient_id: p.patient_id, name: p.name })));
      } catch (e: any) {
        toast.error(t("calendar.errors.loadFailed", { msg: e.message }));
      }
      try {
        setGcal(await api.gcalStatus());
      } catch (e: any) {
        toast.error(t("calendar.errors.loadFailed", { msg: e.message }));
      }
      // Returning from the Google consent flow: the callback redirects to
      // #tab=calendar&gcal=connected|error — surface it once, then clean up.
      const hash = window.location.hash;
      const m = /[&#]gcal=(connected|error)/.exec(hash);
      if (m) {
        if (m[1] === "connected") toast.success(t("calendar.gcal.connectedToast"));
        else toast.error(t("calendar.gcal.errorToast"));
        window.location.hash = hash.replace(/&?gcal=(connected|error)/, "");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGcalConnect = async () => {
    try {
      const { auth_url } = await api.gcalConnect();
      window.location.assign(auth_url);
    } catch (e: any) {
      toast.error(t("calendar.errors.saveFailed", { msg: e.message }));
    }
  };

  const onGcalDisconnect = async () => {
    try {
      await api.gcalDisconnect();
      setGcal({ connected: false, failed_count: 0 });
    } catch (e: any) {
      toast.error(t("calendar.errors.saveFailed", { msg: e.message }));
    }
  };

  const mutate = useCallback(
    async (
      optimistic: (appts: Appointment[]) => Appointment[],
      run: () => Promise<{ warnings: string[] }>,
      failKey: string,
    ) => {
      setWeek({ ...week, appointments: optimistic(week.appointments) });
      try {
        const { warnings } = await run();
        surfaceWarnings(warnings);
        await refresh(); // authoritative re-fetch
      } catch (e: any) {
        // Roll back by re-fetching, not by restoring a snapshot: a snapshot
        // captured before a CONCURRENT mutation succeeded would wipe that
        // mutation's result from the screen (review M1-#4).
        toast.error(t(failKey, { msg: e.message }));
        await refresh();
      }
    },
    [week, refresh, surfaceWarnings, toast, t],
  );

  const onMove = (id: string, when: Date) =>
    mutate(
      (appts) =>
        appts.map((a) => {
          if (a.appointment_id !== id) return a;
          const dur = new Date(a.end_at).getTime() - new Date(a.start_at).getTime();
          return {
            ...a,
            start_at: when.toISOString(),
            end_at: new Date(when.getTime() + dur).toISOString(),
          };
        }),
      () => api.move(id, when.toISOString()),
      "calendar.errors.moveFailed",
    );

  const onResize = (id: string, durationMin: number) =>
    mutate(
      (appts) =>
        appts.map((a) =>
          a.appointment_id === id
            ? { ...a, end_at: new Date(new Date(a.start_at).getTime() + durationMin * 60_000).toISOString() }
            : a,
        ),
      () => api.resize(id, durationMin),
      "calendar.errors.saveFailed",
    );

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setCreateAt(null);
    setCreateDur(null);
  };

  const onCreate = (payload: CreateAppointmentPayload) => {
    closeDialog();
    mutate((a) => a, () => api.create(payload), "calendar.errors.saveFailed");
  };

  const onEdit = (id: string, body: Record<string, unknown>, scope: EditScope) => {
    closeDialog();
    mutate((a) => a, () => api.patch(id, body, scope), "calendar.errors.saveFailed");
  };

  const onCancelAppt = (id: string, notify: boolean) => {
    closeDialog();
    mutate((a) => a, () => api.cancel(id, notify), "calendar.errors.saveFailed");
  };

  const onDelete = (id: string, scope: EditScope, notify: boolean) => {
    closeDialog();
    mutate((a) => a, () => api.remove(id, scope, notify), "calendar.errors.saveFailed");
  };

  const onCreateInvoice = async (id: string, grossHuf: number) => {
    closeDialog();
    try {
      const r = await api.createInvoice(id, grossHuf);
      surfaceWarnings(r.warnings);
      toast.success(t("calendar.invoicing.created", { number: r.invoice.invoice_number }));
    } catch (e: any) {
      toast.error(t("calendar.invoicing.failed", { msg: e.message }));
    }
    await refresh();
  };

  const onStornoInvoice = async (id: string) => {
    closeDialog();
    try {
      const r = await api.stornoInvoice(id);
      surfaceWarnings(r.warnings);
      toast.success(t("calendar.invoicing.stornoDone", { number: r.invoice.invoice_storno_number }));
    } catch (e: any) {
      toast.error(t("calendar.invoicing.failed", { msg: e.message }));
    }
    await refresh();
  };

  const patientNames = useMemo(
    () => Object.fromEntries(patients.map((p) => [p.patient_id, p.name])),
    [patients],
  );

  const weekLabel = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language, { month: "short", day: "numeric" });
    return `${fmt.format(days[0])} – ${fmt.format(days[6])}`;
  }, [days, i18n.language]);

  const effectiveSettings: SchedulingSettings = settings ?? {
    work_start: "08:00", work_end: "18:00",
    default_duration_min: 50, default_reminder_lead_min: 1440,
    timezone: "Europe/Budapest",
  };

  return (
    <div className="cal-wrap" data-testid="calendar-page">
      <div className="cal-toolbar">
        <span className="cal-title">{weekLabel}</span>
        <div className="cal-nav">
          <button type="button" className="cal-nav__arrow" aria-label={t("calendar.prevWeek")}
            onClick={() => setAnchor((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}>‹</button>
          <button type="button" onClick={() => setAnchor(new Date())}>{t("calendar.today")}</button>
          <button type="button" className="cal-nav__arrow" aria-label={t("calendar.nextWeek")}
            onClick={() => setAnchor((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}>›</button>
        </div>
        <button type="button" className="btn btn--primary btn--sm"
          onClick={() => { setCreateAt(new Date()); setCreateDur(null); setDialogOpen(true); }}>
          {t("calendar.newAppointment")}
        </button>
        {gcal && !gcal.connected && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={onGcalConnect}
            data-testid="gcal-connect">
            {t("calendar.gcal.connect")}
          </button>
        )}
        {gcal?.connected && (
          <button type="button"
            className={`btn btn--sm${gcal.failed_count ? " btn--danger" : ""}`}
            onClick={onGcalDisconnect} data-testid="gcal-chip"
            title={t("calendar.gcal.disconnect")}>
            {gcal.failed_count
              ? t("calendar.gcal.failedCount", { count: gcal.failed_count })
              : t("calendar.gcal.connected")}
          </button>
        )}
        <button type="button" className="icon-btn" aria-label={t("calendar.settings.title")}
          onClick={() => setSettingsOpen(true)}>⚙</button>
      </div>

      {loadError && <div className="cal-error" role="alert">{t("calendar.errors.loadFailed", { msg: loadError })}</div>}

      <WeekGrid
        anchor={anchor}
        workStart={effectiveSettings.work_start}
        workEnd={effectiveSettings.work_end}
        appointments={week.appointments}
        series={week.series}
        patientNames={patientNames}
        onMove={onMove}
        onResize={onResize}
        onSelect={(a) => { setEditing(a); setDialogOpen(true); }}
        onCreateAt={(when) => { setCreateAt(when); setCreateDur(null); setDialogOpen(true); }}
        onCreateRange={(when, durationMin) => {
          setCreateAt(when);
          setCreateDur(durationMin);
          setDialogOpen(true);
        }}
      />

      <AppointmentDialog
        open={dialogOpen}
        appointment={editing}
        createAt={createAt}
        createDurationMin={createDur}
        invoicing={invoicing}
        onCreateInvoice={onCreateInvoice}
        onStornoInvoice={onStornoInvoice}
        onOpenInvoicingSettings={() => { setDialogOpen(false); setSettingsOpen(true); }}
        isSeriesMember={Boolean(
          editing?.series_id && week.series[editing.series_id] && !editing?.detached,
        )}
        patients={patients}
        settings={effectiveSettings}
        onClose={closeDialog}
        onCreate={onCreate}
        onEdit={onEdit}
        onCancelAppt={onCancelAppt}
        onDelete={onDelete}
      />

      {settingsOpen && settings && (
        <SettingsDialog
          settings={settings}
          invoicing={invoicing}
          onClose={() => setSettingsOpen(false)}
          onSave={async (s, inv) => {
            setSettingsOpen(false);
            try {
              const { warnings } = await api.saveSettings(s);
              surfaceWarnings(warnings);
              setSettings(s);
            } catch (e: any) {
              toast.error(t("calendar.errors.saveFailed", { msg: e.message }));
            }
            try {
              const r = await api.saveInvoicing(inv);
              surfaceWarnings(r.warnings);
              setInvoicing(r.settings);
            } catch (e: any) {
              toast.error(t("calendar.invoicing.failed", { msg: e.message }));
            }
          }}
        />
      )}
    </div>
  );
}
